// -----------------------------------------------------------------------------------------------------

export class AuthUtils {
    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Is token expired?
     *
     * @param token
     * @param offsetSeconds
     */
    static isTokenExpired(token: string, offsetSeconds?: number): boolean {

        // Return if there is no token
        if (!token || token === '') {
            return false;
        }
        // Ensure the date string is treated as UTC (append Z if missing)
        const raw = localStorage.getItem('expirationDate');
        const utcString = raw && !raw.endsWith('Z') ? raw + 'Z' : raw;
        const date = new Date(utcString);

        offsetSeconds = offsetSeconds || 0;

        if (date === null) {
            return true;
        }
        // Check if the token is expired
        return date.valueOf() <= new Date().valueOf() + offsetSeconds * 1000;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Base64 decoder
     * Uses native atob when available, with fallback for environments without it
     *
     * @param str
     * @private
     */
    private static _b64decode(str: string): string {
        const input = String(str).replace(/=+$/, '');

        if (input.length % 4 === 1) {
            throw new Error('\'atob\' failed: The string to be decoded is not correctly encoded.');
        }

        // Use native atob if available (browser environment)
        if (typeof atob === 'function') {
            return atob(str);
        }

        // Fallback implementation for non-browser environments
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=';
        let output = '';
        let bitCounter = 0;
        let bitStorage = 0;

        for (let idx = 0; idx < input.length; idx++) {
            const charIndex = chars.indexOf(input.charAt(idx));

            if (charIndex === -1) {
                continue;
            }

            if (bitCounter % 4 === 0) {
                bitStorage = charIndex;
            } else {
                bitStorage = bitStorage * 64 + charIndex;
                const shift = (-2 * (bitCounter + 1)) & 6;
                output += String.fromCharCode(255 & (bitStorage >> shift));
            }

            bitCounter++;
        }

        return output;
    }

    /**
     * Base64 unicode decoder
     *
     * @param str
     * @private
     */
    private static _b64DecodeUnicode(str: any): string {
        return decodeURIComponent(
            Array.prototype.map
                .call(this._b64decode(str), (c: any) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
    }

    /**
     * URL Base 64 decoder
     *
     * @param str
     * @private
     */
    private static _urlBase64Decode(str: string): string {
        let output = str.replace(/-/g, '+').replace(/_/g, '/');
        switch (output.length % 4) {
            case 0:
                {
                    break;
                }
            case 2:
                {
                    output += '==';
                    break;
                }
            case 3:
                {
                    output += '=';
                    break;
                }
            default:
                {
                    throw Error('Illegal base64url string!');
                }
        }
        return this._b64DecodeUnicode(output);
    }

    /**
     * Decode token
     *
     * @param token
     * @private
     */
    private static _decodeToken(token: string): any {
        // Return if there is no token
        if (!token) {
            return null;
        }

        // Split the token
        const parts = token.split('.');

        if (parts.length !== 3) {
            throw new Error('The inspected token doesn\'t appear to be a JWT. Check to make sure it has three parts and see https://jwt.io for more.');
        }

        // Decode the token using the Base64 decoder
        const decoded = this._urlBase64Decode(parts[1]);

        if (!decoded) {
            throw new Error('Cannot decode the token.');
        }

        return JSON.parse(decoded);
    }

    /**
     * Get token expiration date
     *
     * @param token
     * @private
     */
    private static _getTokenExpirationDate(token: string): Date | null {
        // Get the decoded token
        const decodedToken = this._decodeToken(token);

        // Return if the decodedToken doesn't have an 'exp' field
        if (!decodedToken.hasOwnProperty('exp')) {
            return null;
        }

        // Convert the expiration date
        const date = new Date(0);
        date.setUTCSeconds(decodedToken.exp);

        return date;
    }
}
